import { useParams, useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Paper } from "@mantine/core";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/navbar/navbar";
import { ReferPatientProgressTracker } from "@/components/refer-patient-progress-tracker";
import { StrengthenReferral } from "../strengthen-referral/strengthen-referral";
import { PageFooter } from "@/components/layout/page-footer";
import InsuranceInfoForm, {
  InsuranceInfoFormHandle,
} from "@/components/insurance-info-form/insurance-info-form";
import styles from "../strengthen-referral/styles/tab-content.module.scss";

export const GET_APPEAL_STATUS = gql(`
  query GetAppealStatus($appealId: ID!) {
    appealStatus(appealId: $appealId) {
      referred
    }
  }
`);

export function Insurance() {
  const { appealId } = useParams<{ appealId: string }>();
  const formRef = useRef<InsuranceInfoFormHandle>(null);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, loading: statusLoading } = useQuery(GET_APPEAL_STATUS, {
    variables: { appealId: appealId! },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  const readOnly = data?.appealStatus?.referred ?? false;

  if (!appealId) {
    return null;
  }

  const handleSave = async (shouldNavigate = true) => {
    setIsSubmitting(true);
    try {
      const success = await formRef.current?.submit();
      if (success && shouldNavigate) {
        navigate(`/appeals/${appealId}/strengthen/documents`);
      }
      return success ?? false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveBeforeSend = async () => {
    return await handleSave(false);
  };

  const footer =
    !statusLoading && !readOnly ? (
      <PageFooter
        primaryButton={{
          text: "Save",
          onClick: handleSave,
          loading: isSubmitting,
          disabled: isSubmitting,
        }}
      />
    ) : undefined;

  return (
    <Navbar
      titleComponent={
        <ReferPatientProgressTracker
          appealId={appealId}
          onBeforeSendReferral={handleSaveBeforeSend}
        />
      }
      footer={footer}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="insurance"
        tabContent={
          <Paper className={styles.container} p="md">
            <InsuranceInfoForm
              ref={formRef}
              appealId={appealId}
              readOnly={readOnly}
            />
          </Paper>
        }
      />
    </Navbar>
  );
}
